import { useState, FC, createContext, useEffect, useContext, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import type { Location } from 'history';

interface RouteContextValue {
    ref: Location<unknown>[];
    routes: Location<unknown>[];
}

export const RouteContext = createContext<RouteContextValue>({ ref: [], routes: [] });

export const useRoute = (): RouteContextValue => useContext(RouteContext);

export const RouteProvider: FC = ({ children }) => {
    const history = useHistory();
    const routesRef = useRef([] as Location<unknown>[]);
    const [routes, setRoutes] = useState([...routesRef.current]);
    const value = useMemo(() => ({ ref: routesRef.current, routes }), [routes]);

    useEffect(() => {
        const unregister = history.listen((location, action) => {
            if (action === 'POP') {
                routesRef.current.pop();
            } else if (action === 'PUSH') {
                routesRef.current.push({ ...location });
            } else if (action === 'REPLACE') {
                routesRef.current[routesRef.current.length - 1] = { ...location };
            }

            setRoutes([...routesRef.current]);
        });

        return () => unregister();
    }, [history]);

    return <RouteContext.Provider value={value}>{children}</RouteContext.Provider>;
};

export const useLastRoute = (): Location<unknown> | null => {
    const { routes } = useRoute();

    if (routes.length <= 1) return null;

    return routes[routes.length - 2] ?? null;
};

export const useCurrentRoute = (): Location<unknown> | null => {
    const { routes } = useRoute();

    if (routes.length === 0) return null;

    return routes[routes.length - 1] ?? null;
};
